
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MonthlyRevenue {
  month: number;
  revenue: number;
  pending_revenue: number;
}

export function useMonthlyRevenue() {
  return useQuery({
    queryKey: ['monthly-revenue'],
    queryFn: async (): Promise<MonthlyRevenue[]> => {
      try {
        // First check if user is authenticated
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('Authentication error:', sessionError);
          return generateEmptyMonthlyData();
        }
        
        if (!sessionData.session) {
          console.error('No session found');
          return generateEmptyMonthlyData();
        }
        
        const currentYear = new Date().getFullYear();
        const { data, error } = await supabase
          .rpc('get_monthly_revenue', { year_param: currentYear });

        if (error) {
          console.error('Error fetching monthly revenue:', error);
          return generateEmptyMonthlyData();
        }
        
        // Make sure revenue values are properly parsed as numbers
        const formattedData = (data || []).map(item => ({
          month: item.month,
          revenue: Number(item.revenue) || 0,
          pending_revenue: Number(item.pending_revenue) || 0
        }));

        console.log('Monthly revenue data:', formattedData);
        
        return formattedData.length > 0 ? formattedData : generateEmptyMonthlyData();
      } catch (error) {
        console.error('Error in monthly revenue query:', error);
        return generateEmptyMonthlyData();
      }
    },
    // Don't retry on error
    retry: 1,
    // Wait for authentication before fetching
    enabled: true,
    // Return empty data if there's an error
    gcTime: 1000 * 60 * 5, // Keep data for 5 minutes
  });
}

// Helper function to generate empty data for all months
function generateEmptyMonthlyData(): MonthlyRevenue[] {
  return Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    revenue: 0,
    pending_revenue: 0
  }));
}
